/**
 * Áreas Funcionais — tela de operação do tenant (app).
 *
 * Modelo (definido em 24/09/2026):
 * - A entidade é por GRUPO de empresas; a EMPRESA do contexto é o eixo de leitura.
 * - O admin do grupo (nível 1) mantém as áreas; perfis somente leitura (ex.: GERENTE) apenas consultam.
 * - Como a área vale para todas as empresas do grupo, a tela avisa quantas empresas são afetadas.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Download,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { GridArchetype } from '../components/archetypes/GridArchetype'
import { useSignature } from '../contexts/SignatureContext'
import { useAuth } from '../contexts/AuthContext'
import { areasFuncionaisApi } from '../api/areasFuncionais'
import { AreaFuncional, AreaFuncionalCreate } from '../types/api'
import { exportToExcel } from '../utils/exportToExcel'

type ToastType = 'success' | 'error' | 'warning'
interface Toast { id: number; msg: string; type: ToastType }

const extrairMensagemErro = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim().length > 0) return detail
  if (Array.isArray(detail) && detail.length > 0) return detail[0]?.msg || fallback
  return error?.message || fallback
}

export default function AreasFuncionaisPage() {
  const { colors } = useSignature()
  const { getEmpresaId, getEmpresaNome } = useAuth()
  const queryClient = useQueryClient()

  const empresaId = getEmpresaId()
  const empresaNome = getEmpresaNome()

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<AreaFuncional | null>(null)
  const [editMode, setEditMode] = useState<'none' | 'new' | 'edit'>('none')
  const [form, setForm] = useState({ nome: '', descricao: '', iduser: '' })
  const [areaParaExcluir, setAreaParaExcluir] = useState<AreaFuncional | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const toastId = useRef(0)

  const toast = useCallback((msg: string, type: ToastType = 'success') => {
    const id = ++toastId.current
    setToasts((atual) => [...atual, { id, msg, type }])
    setTimeout(() => setToasts((atual) => atual.filter((item) => item.id !== id)), 4000)
  }, [])

  const escopoQuery = useQuery({
    queryKey: ['areas-funcionais-escopo', empresaId],
    queryFn: () => areasFuncionaisApi.escopo(empresaId),
    retry: false,
  })

  const escopo = escopoQuery.data
  const podeEscrever = Boolean(escopo?.pode_escrever)

  const areasQuery = useQuery({
    queryKey: ['areas-funcionais', empresaId],
    queryFn: () => areasFuncionaisApi.list({ empresa_id: empresaId ?? undefined }),
    retry: false,
  })

  const areas = areasQuery.data ?? []

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['areas-funcionais'] })
    queryClient.invalidateQueries({ queryKey: ['areas-funcionais-escopo'] })
  }

  const createMutation = useMutation({
    mutationFn: (data: AreaFuncionalCreate) => areasFuncionaisApi.create(data),
    onSuccess: () => {
      invalidar()
      setEditMode('none')
      setSelected(null)
      toast('Área funcional criada com sucesso')
    },
    onError: (error: any) => toast(extrairMensagemErro(error, 'Erro ao criar área funcional'), 'error'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AreaFuncionalCreate> }) =>
      areasFuncionaisApi.update(id, data),
    onSuccess: () => {
      invalidar()
      setEditMode('none')
      setSelected(null)
      toast('Área funcional atualizada com sucesso')
    },
    onError: (error: any) => toast(extrairMensagemErro(error, 'Erro ao atualizar área funcional'), 'error'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => areasFuncionaisApi.delete(id),
    onSuccess: () => {
      invalidar()
      setAreaParaExcluir(null)
      setSelected(null)
      toast('Área funcional excluída com sucesso')
    },
    onError: (error: any) => toast(extrairMensagemErro(error, 'Erro ao excluir área funcional'), 'error'),
  })

  const salvando = createMutation.isPending || updateMutation.isPending

  const areasFiltradas = useMemo(
    () =>
      areas.filter((area) => {
        const termo = search.trim().toLowerCase()
        if (!termo) return true
        return (
          area.nome.toLowerCase().includes(termo) ||
          (area.descricao || '').toLowerCase().includes(termo)
        )
      }),
    [areas, search]
  )

  const handleNovo = () => {
    if (!escopo?.grupo_id) {
      toast('Nenhum grupo resolvido para o contexto atual', 'warning')
      return
    }
    setSelected(null)
    setForm({ nome: '', descricao: '', iduser: '' })
    setEditMode('new')
  }

  const handleEditar = () => {
    if (!selected) return
    setForm({
      nome: selected.nome,
      descricao: selected.descricao || '',
      iduser: selected.iduser != null ? String(selected.iduser) : '',
    })
    setEditMode('edit')
  }

  const handleCancelar = () => {
    setEditMode('none')
    setForm({ nome: '', descricao: '', iduser: '' })
  }

  const handleSalvar = async () => {
    if (!form.nome.trim()) {
      toast('Nome é obrigatório', 'warning')
      return
    }

    const iduser = form.iduser.trim() === '' ? null : Number(form.iduser)
    if (iduser != null && Number.isNaN(iduser)) {
      toast('ID User deve ser numérico', 'warning')
      return
    }

    if (editMode === 'new') {
      if (!escopo?.grupo_id) {
        toast('Grupo não resolvido para criar a área', 'error')
        return
      }
      await createMutation.mutateAsync({
        grupo_id: escopo.grupo_id,
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        iduser,
      })
      return
    }

    if (editMode === 'edit' && selected) {
      await updateMutation.mutateAsync({
        id: selected.id,
        data: {
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || null,
          iduser,
        },
      })
    }
  }

  const handleExportExcel = () => {
    const columns = [
      { key: 'id', label: 'ID' },
      { key: 'grupo', label: 'Grupo' },
      { key: 'nome', label: 'Nome' },
      { key: 'descricao', label: 'Descrição' },
      { key: 'iduser', label: 'ID User' },
    ]
    const rows = areasFiltradas.map((area) => ({
      id: area.id,
      grupo: escopo?.grupo_nome || `Grupo ${area.grupo_id}`,
      nome: area.nome,
      descricao: area.descricao || '',
      iduser: area.iduser ?? '',
    }))

    exportToExcel(rows, columns, {
      filename: `areas_funcionais_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: 'Áreas Funcionais',
    })
    toast('Dados exportados para Excel!')
  }

  // Erro de acesso (403/401) ou falha de carregamento
  const erroCarregamento = areasQuery.error || escopoQuery.error
  if (erroCarregamento) {
    return (
      <GridArchetype title="Áreas Funcionais" subtitle={`Empresa: ${empresaNome || 'Contexto ativo'}`}>
        <div className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} style={{ color: colors.warning }} />
            <div>
              <p className="text-sm font-bold" style={{ color: colors.textPrimary }}>
                Não foi possível carregar as áreas funcionais
              </p>
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                {extrairMensagemErro(erroCarregamento, 'Erro ao carregar áreas funcionais')}
              </p>
            </div>
          </div>
        </div>
      </GridArchetype>
    )
  }

  const carregando = areasQuery.isLoading || escopoQuery.isLoading
  const avisarGrupoCompartilhado = (escopo?.qtd_empresas_grupo || 0) > 1

  return (
    <>
      <GridArchetype
        title="Áreas Funcionais"
        subtitle={`Empresa: ${empresaNome || escopo?.empresa_nome || 'Contexto ativo'} · Grupo: ${escopo?.grupo_nome || '—'}`}
        gridActions={
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              disabled={areasFiltradas.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-40 text-white rounded text-xs font-bold transition-colors cursor-pointer shadow-xs"
              style={{ backgroundColor: colors.success }}
              title="Exportar para Excel (.xlsx)"
            >
              <Download size={14} /> Excel
            </button>
            <span className="text-xs font-mono ml-2" style={{ color: colors.textSecondary }}>
              {carregando ? 'Carregando...' : `${areasFiltradas.length} de ${areas.length} registros`}
            </span>
          </div>
        }
        filterPanel={
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {podeEscrever ? (
                  <>
                    <button
                      onClick={handleNovo}
                      disabled={editMode !== 'none'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
                      style={{ backgroundColor: colors.success }}
                    >
                      <Plus size={14} /> Novo
                    </button>
                    <button
                      onClick={handleEditar}
                      disabled={!selected || editMode !== 'none'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Pencil size={14} /> Editar
                    </button>
                    <button
                      onClick={() => selected && setAreaParaExcluir(selected)}
                      disabled={!selected || editMode !== 'none'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs disabled:opacity-40 text-white"
                      style={{ backgroundColor: colors.danger }}
                    >
                      <Trash2 size={14} /> Excluir
                    </button>
                  </>
                ) : (
                  <span
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full border"
                    style={{
                      color: colors.warning,
                      borderColor: colors.warning,
                      backgroundColor: colors.bgSurfaceHover,
                    }}
                    title="Seu perfil permite somente consulta"
                  >
                    Somente leitura
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 flex-1 justify-end max-w-md">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou descrição..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 rounded-xl border text-xs focus:outline-none"
                    style={{
                      backgroundColor: colors.bgSurface,
                      borderColor: colors.border,
                      color: colors.textPrimary,
                    }}
                  />
                </div>
                <button
                  onClick={() => { areasQuery.refetch(); escopoQuery.refetch() }}
                  disabled={carregando}
                  className="p-1.5 rounded-xl border text-xs font-bold transition-colors cursor-pointer"
                  style={{
                    backgroundColor: colors.bgSurface,
                    borderColor: colors.border,
                    color: colors.textPrimary,
                  }}
                  title="Recarregar dados"
                >
                  <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {/* A área é do grupo: avisa quantas empresas compartilham o mesmo conjunto */}
            {avisarGrupoCompartilhado && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-xl border text-[11px]"
                style={{ borderColor: colors.warning, backgroundColor: colors.bgSurfaceHover, color: colors.textSecondary }}
              >
                <AlertTriangle size={14} style={{ color: colors.warning }} className="mt-0.5 shrink-0" />
                <span>
                  Estas áreas valem para as <strong>{escopo?.qtd_empresas_grupo}</strong> empresas do grupo{' '}
                  <strong>{escopo?.grupo_nome}</strong>
                  {podeEscrever
                    ? ': qualquer alteração aqui afeta todas elas.'
                    : ': seu perfil permite apenas consulta.'}
                </span>
              </div>
            )}
          </div>
        }
      >
        {/* Formulário inline */}
        {editMode !== 'none' && (
          <div
            className="p-4 border-b flex items-end gap-4 shrink-0"
            style={{ backgroundColor: colors.primaryLight, borderColor: colors.border }}
          >
            <div className="flex-1 max-w-md">
              <label className="block text-xs font-bold mb-1" style={{ color: colors.textSecondary }}>
                Nome *
              </label>
              <input
                type="text"
                maxLength={100}
                value={form.nome}
                onChange={(event) => setForm((atual) => ({ ...atual, nome: event.target.value }))}
                placeholder="Ex.: Financeiro"
                className="w-full px-3 py-1.5 rounded-xl border text-xs focus:outline-none"
                style={{
                  backgroundColor: colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              />
            </div>

            <div className="flex-1 max-w-lg">
              <label className="block text-xs font-bold mb-1" style={{ color: colors.textSecondary }}>
                Descrição
              </label>
              <input
                type="text"
                maxLength={500}
                value={form.descricao}
                onChange={(event) => setForm((atual) => ({ ...atual, descricao: event.target.value }))}
                placeholder="Descrição da área funcional"
                className="w-full px-3 py-1.5 rounded-xl border text-xs focus:outline-none"
                style={{
                  backgroundColor: colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              />
            </div>

            <div>
              <label className="block text-xs font-bold mb-1" style={{ color: colors.textSecondary }}>
                ID User
              </label>
              <input
                type="number"
                value={form.iduser}
                onChange={(event) => setForm((atual) => ({ ...atual, iduser: event.target.value }))}
                placeholder="opcional"
                className="w-24 px-3 py-1.5 rounded-xl border text-xs font-mono focus:outline-none"
                style={{
                  backgroundColor: colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSalvar}
                disabled={salvando}
                className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-bold text-white cursor-pointer disabled:opacity-40"
                style={{ backgroundColor: colors.success }}
              >
                {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
              </button>
              <button
                onClick={handleCancelar}
                className="flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-bold border cursor-pointer"
                style={{
                  backgroundColor: colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <X size={14} /> Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Tabela */}
        <div className="overflow-y-auto flex-1 scrollbar-standard">
          <table className="w-full text-xs text-left">
            <thead
              className="uppercase text-[11px] font-bold tracking-wider sticky top-0 border-b z-10"
              style={{ backgroundColor: colors.bgApp, borderColor: colors.border, color: colors.textSecondary }}
            >
              <tr>
                <th className="px-4 py-3 w-20">ID</th>
                <th className="px-4 py-3 w-48">Grupo</th>
                <th className="px-4 py-3 w-56">Nome</th>
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3 w-24">ID User</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: colors.border }}>
              {carregando ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center font-semibold" style={{ color: colors.textMuted }}>
                    Carregando...
                  </td>
                </tr>
              ) : areasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center font-semibold" style={{ color: colors.textMuted }}>
                    Nenhuma área funcional encontrada.
                  </td>
                </tr>
              ) : (
                areasFiltradas.map((area) => {
                  const isSelecionada = selected?.id === area.id
                  return (
                    <tr
                      key={area.id}
                      onClick={() => setSelected(area)}
                      className={`cursor-pointer transition-all hover:bg-blue-500/10 ${
                        isSelecionada ? 'border-l-4 border-l-blue-500 bg-blue-500/15 font-semibold' : ''
                      }`}
                      style={{
                        backgroundColor: isSelecionada ? undefined : colors.bgSurface,
                        color: colors.textPrimary,
                      }}
                    >
                      <td className="px-4 py-3 font-mono font-bold">{area.id}</td>
                      <td className="px-4 py-3">{escopo?.grupo_nome || `Grupo ${area.grupo_id}`}</td>
                      <td className="px-4 py-3 font-semibold">{area.nome}</td>
                      <td className="px-4 py-3">{area.descricao || '—'}</td>
                      <td className="px-4 py-3 font-mono">{area.iduser ?? '—'}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Rodapé */}
        <div
          className="px-4 py-2 border-t flex items-center justify-between text-xs font-mono shrink-0"
          style={{ backgroundColor: colors.bgApp, borderColor: colors.border, color: colors.textSecondary }}
        >
          <span>{carregando ? 'Carregando...' : `Exibindo ${areasFiltradas.length} de ${areas.length} registros`}</span>
          {selected && (
            <span style={{ color: colors.primary }} className="font-semibold">
              Selecionada: <code className="font-bold">{selected.nome}</code>
            </span>
          )}
        </div>
      </GridArchetype>

      {/* Confirmação de exclusão */}
      {areaParaExcluir && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="w-full max-w-md rounded-2xl border p-5 shadow-xl"
            style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}
          >
            <p className="text-sm font-bold" style={{ color: colors.textPrimary }}>
              Excluir área funcional?
            </p>
            <p className="text-xs mt-2" style={{ color: colors.textSecondary }}>
              "{areaParaExcluir.nome}" será removida do grupo <strong>{escopo?.grupo_nome || '—'}</strong>
              {avisarGrupoCompartilhado
                ? ` e deixará de valer para as ${escopo?.qtd_empresas_grupo} empresas do grupo.`
                : '.'}
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setAreaParaExcluir(null)}
                className="px-4 py-1.5 rounded-xl text-xs font-bold border cursor-pointer"
                style={{ backgroundColor: colors.bgSurface, borderColor: colors.border, color: colors.textPrimary }}
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteMutation.mutate(areaParaExcluir.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-1.5 rounded-xl text-xs font-bold text-white cursor-pointer disabled:opacity-40"
                style={{ backgroundColor: colors.danger }}
              >
                {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
          {toasts.map((item) => (
            <div
              key={item.id}
              className="px-4 py-2.5 rounded-xl shadow-lg text-xs font-bold text-white border max-w-sm"
              style={{
                backgroundColor: item.type === 'error' ? colors.danger : item.type === 'warning' ? colors.warning : colors.success,
                borderColor: colors.border,
              }}
            >
              {item.msg}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
